/**
 * useRevenue — 创作者收益总览 + 流水
 *
 * 提供两个独立 hook:
 *   • useRevenueOverview — /api/v1/revenue/summary,卡片头部展示
 *   • useRevenueRecords — /api/v1/revenue/records,分页流水
 *
 * 未登录(enabled=false)时不发起请求。
 */
import { useCallback, useEffect, useState } from 'react'

import {
  ApiError,
  revenueApi,
  type SilkRevenueOverview,
  type SilkRevenueRecord,
  type SilkRevenueSource,
  type SilkRevenueStatus,
} from '@/api'

interface OverviewState {
  data: SilkRevenueOverview | null
  loading: boolean
  error: string | null
}

export function useRevenueOverview(enabled = true) {
  const [state, setState] = useState<OverviewState>({
    data: null,
    loading: false,
    error: null,
  })

  const refresh = useCallback(async () => {
    if (!enabled) return
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const data = await revenueApi.overview()
      setState({ data, loading: false, error: null })
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      setState((s) => ({ ...s, loading: false, error: msg }))
    }
  }, [enabled])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { ...state, refresh }
}

interface RecordsState {
  items: SilkRevenueRecord[]
  total: number
  loading: boolean
  error: string | null
}

interface UseRevenueRecordsOpts {
  source?: SilkRevenueSource
  status?: SilkRevenueStatus
  limit?: number
  offset?: number
  enabled?: boolean
}

export function useRevenueRecords(opts: UseRevenueRecordsOpts = {}) {
  const { source, status, limit = 50, offset = 0, enabled = true } = opts
  const [state, setState] = useState<RecordsState>({
    items: [],
    total: 0,
    loading: false,
    error: null,
  })

  const refresh = useCallback(async () => {
    if (!enabled) return
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const res = await revenueApi.records({ source, status, limit, offset })
      setState({ items: res.items, total: res.total, loading: false, error: null })
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      setState((s) => ({ ...s, loading: false, error: msg }))
    }
  }, [enabled, source, status, limit, offset])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { ...state, refresh }
}
